import axios from "axios";

import store from "store/index";
import { setAPIKey, setIsFetchManagement } from "slices";
import { fetchPropertyData } from "utils";

let isRefreshing = false;
let failedQueue = [];
const MAX_RETRY_ATTEMPTS = 2; // 1 original + 1 retry

// Axios instance
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL,
  timeout: 30000,
});

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => config,
  (error) => Promise.reject(error)
);

// Refresh token logic
const refreshToken = async (config) => {
  try {
    // Fetch the property data to get the API key
    const propertyId = store.getState().property.property_id;
    const res = await fetchPropertyData(propertyId);
    const { api_key } = res.data;

    // Store the API key in the global state
    store.dispatch(setAPIKey(api_key));

    if (config.method === "post" && config.data) {
      let payload;
      payload = JSON.parse(config.data);

      if (payload && typeof payload === "object") {
        if ("graphql_api_key" in payload) {
          payload.graphql_api_key = api_key;
        } else if ("api_key" in payload) {
          payload.api_key = api_key;
        }

        config.data = JSON.stringify(payload);
      } else {
        return Promise.reject("Invalid payload format.");
      }
    } else {
      const updatedUrl = new URL(config.url, axiosInstance.defaults.baseURL);
      updatedUrl.searchParams.set("api_key", api_key);
      config.url = updatedUrl.toString();
    }

    // Send the request using the updated config
    return await axiosInstance(config);
  } catch (err) {
    return Promise.reject(err);
  }
};

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    if (response && (response.status === 401 || response.status === 403)) {
      // Increment retry count
      config._retryCount = (config._retryCount || 0) + 1;

      // Stop retrying after max attempts
      if (config._retryCount >= MAX_RETRY_ATTEMPTS) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        });
      }

      isRefreshing = true;
      store.dispatch(setIsFetchManagement(false));

      try {
        const result = await refreshToken(config);

        failedQueue.forEach(({ resolve }) => resolve(result));
        failedQueue = [];

        return result;
      } catch (err) {
        failedQueue.forEach(({ reject }) => reject(err));
        failedQueue = [];

        return Promise.reject(err);
      } finally {
        isRefreshing = false;
        store.dispatch(setIsFetchManagement(true));
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
